import React, { useMemo } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { isLikelyInternational } from '@/constants/utils/countryDetection';

const createStyles = (colors, isDark, variant) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: variant === 'whop' 
      ? (isDark ? 'rgba(54, 150, 245, 0.1)' : 'rgba(54, 150, 245, 0.08)')
      : (isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)'),
    borderWidth: 1,
    borderColor: variant === 'whop'
      ? (isDark ? 'rgba(54, 150, 245, 0.25)' : 'rgba(54, 150, 245, 0.2)')
      : (isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)'),
  },
  iconContainer: {
    marginTop: 1,
  },
  textContainer: {
    flex: 1,
  },
});

/**
 * InternationalPaymentNotice
 * 
 * A subtle notice component that informs international users about
 * payment options. Shows Whop info when enabled, otherwise Bitcoin info.
 * 
 * Only renders if the user is detected as being outside the US.
 * 
 * @param {Object} props
 * @param {Object} props.style - Additional styles to apply to the container
 * @param {boolean} props.forceShow - Force show the notice (useful for testing)
 * @param {boolean} props.showWhopInfo - Show Whop checkout info instead of Bitcoin
 */
function InternationalPaymentNotice({ style, forceShow = false, showWhopInfo = true }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const variant = showWhopInfo ? 'whop' : 'default';
  const styles = useMemo(() => createStyles(colors, isDark, variant), [colors, isDark, variant]);

  // Only show to international users (or when forced for testing)
  const shouldShow = forceShow || isLikelyInternational();

  if (!shouldShow) {
    return null;
  }

  if (showWhopInfo) {
    return (
      <View style={[styles.container, style]}>
        <View style={styles.iconContainer}>
          <Icon name="globe" size={13} color={colors.text_color_primary} />
        </View>
        <View style={styles.textContainer}>
          <Text size="xs" color="secondary" style={{ lineHeight: 18 }}>
            International checkout powered by Whop. All major cards and payment methods accepted.
          </Text>
        </View>
      </View>
    );
  }

  // On iOS, don't mention Bitcoin as a payment option (App Store compliance)
  if (Platform.OS === 'ios') {
    return (
      <View style={[styles.container, style]}>
        <View style={styles.iconContainer}>
          <Icon name="globe" size={13} color={colors.text_color_tertiary} />
        </View>
        <View style={styles.textContainer}>
          <Text size="xs" color="tertiary" style={{ lineHeight: 18 }}>
            Card payments are US-only. International users can check gab.ai for additional payment options.
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, style]}>
      <View style={styles.iconContainer}>
        <Icon name="globe" size={13} color={colors.text_color_tertiary} />
      </View>
      <View style={styles.textContainer}>
        <Text size="xs" color="tertiary" style={{ lineHeight: 18 }}>
          Card payments are US-only. International users can pay with Bitcoin.
        </Text>
      </View>
    </View>
  );
}

export default React.memo(InternationalPaymentNotice);
